/**
 * The games made for her, as both apps list them.
 *
 * A game made for an activity is a draft that knows its block only by
 * `blockRef` until the server links it, and a game changed by the agent is a
 * new row that supersedes the old one. Listing only a block's `artifactIds`
 * hid every game still being made (and some finished ones that were never
 * linked), so both apps list from here: linked or not, making, ready or
 * failed, one row per version chain, newest first.
 */
import { pictureSrc } from './deckStats'
import { readEngineMaterial } from './engines'
import { WORD_BY_TEXT } from './phonics'
import { sentenceCase } from '../ui'
import { andList } from './path'

/** The fields of a synced artifact row this needs. Both apps' rows fit. */
export interface GameRow {
  _id: string
  blockRef?: string
  deleted?: boolean
  supersedes?: string
  version?: number
  isCurrent?: boolean
  html?: string
  createdAt?: number
  updatedAt?: number
  _creationTime?: number
  generation?: { status?: string } | null
}

export type GameStatus = 'making' | 'ready' | 'failed'

/**
 * Where a game stands. Anything not being made and not failed is ready: an
 * engine game stores only a marker script, a deck or a cards session its
 * data, so a missing html is not a missing game.
 */
export function gameStatus(a: Pick<GameRow, 'generation'>): GameStatus {
  const s = a.generation?.status
  return s === 'generating' ? 'making' : s === 'failed' ? 'failed' : 'ready'
}

const madeAt = (a: GameRow) => a.createdAt ?? a._creationTime ?? a.updatedAt ?? 0

/** The first version of a row's chain, following supersedes back. */
export function chainRoot(a: GameRow, byId: Record<string, GameRow | undefined>): string {
  let at = a
  const seen = new Set<string>()
  while (at.supersedes && byId[at.supersedes] && !seen.has(at._id)) {
    seen.add(at._id)
    at = byId[at.supersedes]!
  }
  return at._id
}

/**
 * One row per version chain, newest first. From each chain the newest ready
 * version, so the game she plays stays playable while a change to it is
 * being made; a chain with nothing ready yet shows its newest row (making,
 * or failed with a retry). `all` resolves chains across rows not in `rows`.
 */
export function latestVersions<A extends GameRow>(rows: A[], all: Record<string, A | undefined> = {}): A[] {
  const byId: Record<string, GameRow | undefined> = { ...all }
  for (const a of rows) byId[a._id] = a
  const chains = new Map<string, A[]>()
  for (const a of rows) {
    if (!a || a.deleted) continue
    const root = chainRoot(a, byId)
    chains.set(root, [...(chains.get(root) ?? []), a])
  }
  const pick = [...chains.values()].map((versions) => {
    const newest = [...versions].sort((x, y) => madeAt(y) - madeAt(x))
    return newest.find((v) => gameStatus(v) === 'ready') ?? newest[0]!
  })
  return pick.sort((x, y) => madeAt(y) - madeAt(x))
}

/**
 * Every game for an activity: the ones linked to the block and the ones made
 * for it and not linked yet (being made, failed, or finished as a draft),
 * one per version chain, newest first. Web blocks carry `id`, phone blocks
 * `_id`.
 */
export function materialsForBlock<A extends GameRow>(
  artifacts: Record<string, A | undefined>,
  block: { _id?: string; id?: string; artifactIds?: string[] },
): A[] {
  const blockId = block._id ?? block.id
  const linked = new Set(block.artifactIds ?? [])
  const byId = artifacts as Record<string, GameRow | undefined>
  const all = Object.values(artifacts).filter((a): a is A => !!a)
  // A change the agent made to one of these games is a new row that may not
  // name the block; it belongs by its chain.
  const roots = new Set(all.filter((a) => linked.has(a._id) || (!!blockId && a.blockRef === blockId)).map((a) => chainRoot(a, byId)))
  return latestVersions(all.filter((a) => roots.has(chainRoot(a, byId))), artifacts)
}

/**
 * One game's page: every version of it oldest first, the one to show (the
 * newest ready version, which she plays until a change is ready, or the
 * newest row when none is ready yet), and `pending`, a newer version still
 * being made or failed. Versions are ordered by their number, which the
 * server gives each new version above every other in its chain.
 */
export function versionsOf<A extends GameRow>(artifacts: Record<string, A | undefined>, id: string | undefined): { versions: A[]; shown: A | undefined; pending: A | undefined } {
  const start = id ? artifacts[id] : undefined
  if (!start) return { versions: [], shown: undefined, pending: undefined }
  const byId = artifacts as Record<string, GameRow | undefined>
  const root = chainRoot(start, byId)
  const order = (a: A) => [a.version ?? 1, madeAt(a)] as const
  const versions = (Object.values(artifacts) as Array<A | undefined>)
    .filter((a): a is A => !!a && !a.deleted && chainRoot(a, byId) === root)
    .sort((a, b) => order(a)[0] - order(b)[0] || order(a)[1] - order(b)[1])
  const shown = [...versions].reverse().find((v) => gameStatus(v) === 'ready') ?? versions[versions.length - 1]
  const newest = versions[versions.length - 1]
  const pending = newest && shown && newest !== shown && gameStatus(newest) !== 'ready' ? newest : undefined
  return { versions, shown, pending }
}

/** The conversation about a game: the thread whose subject is any version of it, most recently active first. */
export function threadForArtifact<T extends { artifactId?: string; deleted?: boolean; lastMessageAt?: number; updatedAt?: number }>(
  threads: Record<string, T | undefined>,
  versionIds: string[],
): T | undefined {
  const ids = new Set(versionIds)
  const active = (t: T) => t.lastMessageAt ?? t.updatedAt ?? 0
  return (Object.values(threads) as Array<T | undefined>)
    .filter((t): t is T => !!t && !t.deleted && !!t.artifactId && ids.has(t.artifactId))
    .sort((a, b) => active(b) - active(a))[0]
}

/** A version's generation fields that name it, as both apps' rows carry them. */
type Named = { supersedes?: string; generation?: { prompt?: string; label?: string; next?: string[]; nextLabels?: string[]; changes?: string[] } | null }

/**
 * What a version is, in full: for a change, the request the generator was
 * actually given for it (generation.prompt), so a page never claims a change
 * that was asked for in chat but never sent. A first version has none.
 */
export function versionAsk(v: Named | undefined): string | undefined {
  const ask = v?.supersedes ? v.generation?.prompt?.trim() : undefined
  return ask || undefined
}

/**
 * What a change actually made, in the maker's own account of the version it
 * wrote (what she now sees or hears, and anything it could not make). A page
 * shows this over the request when it has it, so a version never claims a
 * change merely because it was asked for. A first version has none.
 */
export function versionChanges(v: Named | undefined): string[] {
  return v?.supersedes ? (v.generation?.changes ?? []).map((c) => c.trim()).filter(Boolean) : []
}

/** A brief's opening clause: what changed, without the spec that follows. */
function headOf(text: string): string {
  const head = text.split(/[.:;(\n]/)[0]!.trim()
  return head.length <= 64 ? head : `${head.slice(0, 64).replace(/\s+\S*$/, '')}…`
}

/**
 * A change's name, the way a parent would say it: the few words Sapling gave
 * the version when it asked for it, else the brief's opening clause. A first
 * version has no name of its own (the page calls it the first version).
 */
export function versionLabel(v: Named | undefined): string | undefined {
  const label = v?.supersedes ? v.generation?.label?.trim() : undefined
  if (label) return sentenceCase(label)
  const ask = versionAsk(v)
  return ask ? sentenceCase(headOf(ask)) : undefined
}

/**
 * What a game is called on screen, the same in both apps. A ready game has
 * its own name. A change still being made is the same game, so it wears the
 * name of the version that plays (`playing`). A new game has no name yet:
 * made for an activity it is that activity's game; otherwise the few words
 * Sapling named it with, else the opening clause of what was asked. The
 * words sent to the maker are a brief (her age, word lists, what scares
 * her), never a name, so they are never shown whole.
 */
export function gameTitle(
  a: { title?: string; generation?: { status?: string; prompt?: string; label?: string } | null },
  around: { playing?: string; activity?: string } = {},
): string {
  if (gameStatus(a) === 'ready') return a.title ?? ''
  if (around.playing) return around.playing
  if (around.activity) return `A game for ${around.activity}`
  const label = a.generation?.label?.trim()
  if (label) return sentenceCase(label)
  const asked = (a.generation?.prompt ?? a.title ?? '').replace(/\s*\([^)]*\)/g, '')
  return sentenceCase(headOf(asked)) || 'A new game'
}

/**
 * A few changes a parent is likely to want for this game, as tappable
 * starts drawn from the game itself: more of what it is about, then its own
 * levers (the challenge, the pace). They fill the box; nothing is sent until
 * the parent sends it.
 */
export function changeIdeas(game: { title?: string; html?: string; generation?: { prompt?: string } | null }, opts: { book?: boolean; name?: string } = {}): string[] {
  const { cover } = gameCover(game, opts.name)
  const thing = cover && /^[a-z]+$/.test(cover) ? cover : undefined
  const more = thing ? (/^(fish|sheep|deer)$/.test(thing) ? thing : /(s|sh|ch|x)$/.test(thing) ? `${thing}es` : `${thing}s`) : undefined
  return [
    more ? `More ${more}` : opts.book ? 'One more page' : 'Make it a little harder',
    'Make it easier',
    'Slower, with more time to answer',
  ]
}

/**
 * The changes waiting on a version being made, by name, in the order they
 * were asked: they become the next version, together, the moment this one
 * settles. Named by Sapling's label when it gave one, else by each ask's
 * opening clause.
 */
export function waitingChanges(v: Named | undefined): string[] {
  const g = v?.generation
  const labels = (g?.nextLabels ?? []).map((l) => l.trim()).filter(Boolean)
  if (labels.length) return labels.map(sentenceCase)
  return (g?.next ?? []).map(headOf).filter(Boolean).map(sentenceCase)
}

/** Whether she has played it, as both apps say it: "played once", "played 3 times", "not played yet". */
export function playedLabel(a: { playCount?: number }): string {
  const n = a.playCount ?? 0
  return n === 1 ? 'played once' : n === 2 ? 'played twice' : n > 2 ? `played ${n} times` : 'not played yet'
}

/**
 * The pictures, lines and clips a ready game asked for that did not come,
 * in words ("2 pictures missing"), or null when nothing is missing. The game
 * still plays (each falls back), but the parent should see it and be able
 * to try again, which retries just those.
 */
export function missingMedia(a: { assets?: Array<{ kind?: string; status?: string }> | null }): string | null {
  const failed = (a.assets ?? []).filter((x) => x.status === 'failed')
  if (!failed.length) return null
  const names: Record<string, [string, string]> = { image: ['picture', 'pictures'], voice: ['voice line', 'voice lines'], video: ['clip', 'clips'] }
  const parts = Object.entries(names).map(([kind, [one, many]]) => {
    const n = failed.filter((x) => x.kind === kind).length
    return n ? `${n} ${n === 1 ? one : many}` : ''
  }).filter(Boolean)
  return `${parts.join(' and ')} missing`
}

/** How long something has really been going, in the words a parent reads: "40s", "2m 05s". */
export function elapsedLabel(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`
}

// Words in the picture bank that name an action or a quality more often
// than a thing in a game's title ("run", "stop", "on top"), so they never
// stand as its cover.
const NOT_A_COVER = new Set(['run', 'sad', 'red', 'check', 'stop', 'top', 'dig', 'drop', 'hug', 'sing', 'swim', 'clap', 'grin', 'kid', 'man', 'six', 'ten', 'light', 'cash'])

/**
 * What small children's games are most often about that the word bank has
 * no clay picture for, as the emoji nearest to it, so a dinosaur game wears
 * a dinosaur rather than a letter.
 */
const THEME_COVERS: Record<string, string> = {
  dinosaur: '🦕', dino: '🦕', rex: '🦖', dragon: '🐉', unicorn: '🦄', bubble: '🫧', jellyfish: '🪼', octopus: '🐙',
  whale: '🐳', dolphin: '🐬', turtle: '🐢', penguin: '🐧', butterfly: '🦋', ladybug: '🐞', bird: '🐦', chicken: '🐔',
  bear: '🐻', teddy: '🧸', bunny: '🐰', rabbit: '🐰', mouse: '🐭', monkey: '🐒', lion: '🦁', tiger: '🐯', elephant: '🐘',
  giraffe: '🦒', horse: '🐴', pony: '🐴', puppy: '🐶', kitten: '🐱', snake: '🐍', rocket: '🚀', robot: '🤖',
  truck: '🚚', tractor: '🚜', digger: '🚜', plane: '✈️', balloon: '🎈', ball: '⚽', rainbow: '🌈', flower: '🌸',
  garden: '🌷', apple: '🍎', banana: '🍌', cookie: '🍪', cake: '🎂', pizza: '🍕', food: '🍎', snack: '🍪',
  castle: '🏰', crown: '👑', princess: '👑', pirate: '🏴‍☠️', snow: '⛄', snowman: '⛄', leaf: '🍂', leaves: '🍂',
  music: '🎵', song: '🎵', color: '🎨', colour: '🎨', paint: '🎨', shape: '🔷', number: '🔢', counting: '🔢',
}

/** A title or prompt word as the word bank spells it: lower case, no possessive, singular. */
function bankForms(word: string): string[] {
  const w = word.toLowerCase().replace(/[’']s$/, '')
  return [w, w.replace(/es$/, ''), w.replace(/s$/, '')]
}

/**
 * The words of a sentence that can name a thing, leaving out names: in a
 * sentence-case line a capital inside a sentence is a name ("Feed Bun Bun
 * the fish"), and a teddy called Bun Bun is not a bread roll. A title in
 * title case ("The Big Pig") keeps every word, since there a capital is
 * only style.
 */
export function thingWords(text: string): string[] {
  const tokens = [...text.matchAll(/([.!?:]\s*)?([A-Za-z’']+)/g)]
  const lower = tokens.filter((t) => /^[a-z]/.test(t[2]!) && !/^(the|a|an|and|of|in|on|to|for|with)$/.test(t[2]!)).length
  const titleCase = !lower
  return tokens
    .filter((t, i) => titleCase || i === 0 || !!t[1] || !/^[A-Z]/.test(t[2]!))
    .map((t) => t[2]!)
}

/**
 * Why a cover the maker named does not fit the game, or null when it does.
 * A cover is a clay picture from the word bank or one emoji. A bank word
 * must name a thing the game's own words name as a thing, never a sound
 * inside a name: a teddy called Bun Bun wears a teddy, not a bun. The title
 * names the game's subject, so when it names something with a clay picture
 * the cover is that (Feed the hungry snail wears the snail, not the teddy
 * its summary mentions); the summary counts only when the title names
 * nothing drawable.
 */
export function coverProblem(cover: string, said: { title?: string; summary?: string }): string | null {
  const c = cover.trim()
  // A title with a name in it (Feed Bun Bun the fish) may be about the named
  // one, drawn however the game draws them; words after a colon are what she
  // practises (Slide and blend: sun, map, man), not the subject.
  const title = (said.title ?? '').split(':')[0]!
  const named = thingWords(title).length < [...title.matchAll(/[A-Za-z’']+/g)].length - [...title.matchAll(/\b(the|a|an|and|of|in|on|to|for|with)\b/gi)].length
  const subjects = named ? [] : [...new Set(thingWords(title).flatMap(bankForms).filter((f) => pictureSrc(f) && !NOT_A_COVER.has(f)))]
  if (subjects.length && !subjects.includes(c)) return `"${c}" is not the game's subject; its title names ${andList(subjects)}, which has a clay picture, so the cover is ${subjects.length > 1 ? 'one of those' : 'that word'}`
  if (/^[a-z]+$/.test(c)) {
    if (!pictureSrc(c)) return `"${c}" has no clay picture; use a word-bank word that has one, or one emoji`
    const things = thingWords(`${said.title ?? ''}. ${said.summary ?? ''}`).flatMap(bankForms)
    if (!things.includes(c)) return `"${c}" is not a thing the title or summary names (a word inside a name does not count); pick what the game's main character or thing is`
    return null
  }
  return /\p{Extended_Pictographic}/u.test(c) && [...c].length <= 8 ? null : 'cover must be one word-bank word or one emoji'
}

/**
 * A picture made for a game that shows its cover thing (the pink jellyfish
 * she asked for, drawn for this version), or undefined. A game makes
 * pictures of everyone in it, and the one a list shows is its subject, so a
 * picture of a side character (the teddy in the snail game) is never the cover.
 */
export function madeCover<T extends { kind?: string; status?: string; url?: string; id?: string; prompt?: string }>(a: { cover?: string; assets?: T[] | null }): T | undefined {
  const cover = a.cover?.trim().toLowerCase()
  if (!cover || !/^[a-z]+$/.test(cover)) return undefined
  const forms = new Set(bankForms(cover).concat(cover))
  return (a.assets ?? []).find((x) => x?.kind === 'image' && x.status === 'ready' && !!x.url
    && [x.id ?? '', (x.prompt ?? '').split(/[.,]/)[0]!].some((t) => t.toLowerCase().split(/[^a-z]+/).some((w) => forms.has(w) || forms.has(w.replace(/s$/, '')))))
}

/**
 * What a made game wears on its cover, the same in both apps. The cover the
 * maker named for it (what the game shows, chosen with the whole game in
 * view), or a book's own cover word. Otherwise the first thing named in its title, then in
 * what was asked for, that has a clay picture ("fish", "ducks") or a
 * word-bank emoji, then the nearest picture for its theme (a dinosaur,
 * bubbles). When nothing does, `letter` is the first letter of the
 * title after her name: titles begin with it ("Mira's fish bowl"), and a
 * wall of the same initial tells nothing apart.
 */
export function gameCover(
  a: { title?: string; html?: string; cover?: string; generation?: { prompt?: string } | null },
  childName?: string,
): { cover?: string; letter: string } {
  const title = (a.title ?? '').trim()
  const name = childName?.trim()
  const rest = name ? title.replace(new RegExp(`^${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:[’']s)?\\s+`, 'i'), '') : title
  const letter = (rest.replace(/^(the|a|an)\s+/i, '') || title).slice(0, 1).toUpperCase()
  // What the maker named as its cover, then a book's own cover word.
  if (a.cover?.trim()) return { cover: a.cover.trim(), letter }
  const own = typeof a.html === 'string' ? readEngineMaterial(a.html)?.config?.cover : undefined
  if (typeof own === 'string' && own.trim()) return { cover: own.trim(), letter }
  for (const text of [rest, a.generation?.prompt ?? '']) {
    const forms = thingWords(text).flatMap(bankForms).filter((f) => f && !NOT_A_COVER.has(f))
    for (const form of forms) {
      if (pictureSrc(form)) return { cover: form, letter }
      const bank = WORD_BY_TEXT[form]
      if (bank?.emoji) return { cover: bank.emoji, letter }
    }
    // Nothing in the bank: the closest picture for what the game is about.
    const theme = forms.find((f) => THEME_COVERS[f])
    if (theme) return { cover: THEME_COVERS[theme]!, letter }
  }
  return { letter }
}

/**
 * What a game practises, from one short list, so a list of games groups
 * the way a parent thinks of her learning rather than by whatever words the
 * generator chose that day ("Letter sounds" and "First sounds" are one
 * thing). Each names the broad area it belongs to, which goes first in a
 * game's tags with the skill after it. The reading skills are the reading
 * path's own tracks.
 */
export const GAME_SKILLS: Array<{ skill: string; domain: string; also: string[] }> = [
  { skill: 'Letter sounds', domain: 'literacy', also: ['first sounds', 'sounds', 'phonics', 'letters', 'phonemic awareness', 'initial sounds', 'alphabet'] },
  { skill: 'Blending', domain: 'literacy', also: ['reading words', 'reading short words', 'decoding', 'sounding out', 'cvc words'] },
  { skill: 'Word families', domain: 'literacy', also: ['rhyme', 'rhyming', 'onset and rime'] },
  { skill: 'Sight words', domain: 'literacy', also: ['high frequency words'] },
  { skill: 'Stories and books', domain: 'literacy', also: ['reading aloud', 'reading fluency', 'stories', 'books', 'comprehension'] },
  { skill: 'Words and talking', domain: 'language', also: ['vocabulary', 'language', 'speaking', 'listening'] },
  { skill: 'Counting', domain: 'math', also: ['numbers', 'number sense', 'adding', 'addition', 'subtraction'] },
  { skill: 'Shapes and patterns', domain: 'math', also: ['shapes', 'patterns', 'sorting', 'colors', 'colours'] },
  { skill: 'Thinking puzzles', domain: 'math', also: ['logic', 'deduction', 'memory', 'puzzles', 'problem solving'] },
  { skill: 'Nature and science', domain: 'science', also: ['science', 'nature', 'animals', 'weather'] },
  { skill: 'Music and rhythm', domain: 'music', also: ['music', 'rhythm', 'singing'] },
  { skill: 'Drawing and making', domain: 'art', also: ['art', 'drawing', 'making', 'creativity'] },
  { skill: 'Moving', domain: 'motor', also: ['gross motor', 'fine motor', 'catching', 'movement', 'balance'] },
  { skill: 'Feelings and friends', domain: 'social', also: ['feelings', 'emotions', 'social', 'kindness', 'sharing'] },
]

/** One of GAME_SKILLS by its name or a near name the generator or an older game used; undefined when none fits. */
export function gameSkillNamed(name: unknown): (typeof GAME_SKILLS)[number] | undefined {
  const n = typeof name === 'string' ? name.trim().toLowerCase() : ''
  if (!n) return undefined
  return GAME_SKILLS.find((s) => s.skill.toLowerCase() === n) ?? GAME_SKILLS.find((s) => s.also.includes(n))
}

/**
 * A game's tags with its skill from GAME_SKILLS: the domain, then the skill,
 * then its themes. `skill` is what the game was made to practise; `keep` is
 * the skill an earlier version had, which wins, so a game stays in its
 * group through every version. Without either, the first tag that names a
 * skill is used.
 */
export function gameTags(tags: string[], skill?: unknown, keep?: string[]): string[] {
  // A skill named by a tag beats one guessed from a broad area ("language", "logic").
  const areas = new Set([...GAME_SKILLS.map((g) => g.domain), 'language', 'logic', 'reading', 'numeracy', 'maths'])
  const of = (list?: string[]) => {
    const named = (list ?? []).filter((t) => !areas.has(t.trim().toLowerCase()))
    return named.map(gameSkillNamed).find(Boolean) ?? (list ?? []).map(gameSkillNamed).find(Boolean)
  }
  const found = of(keep) ?? gameSkillNamed(skill) ?? of(tags)
  if (!found) return tags
  const rest = tags.filter((t) => !areas.has(t.trim().toLowerCase()) && gameSkillNamed(t) !== found)
  return [found.domain, found.skill, ...rest]
}
